// MP251 — Athena copilot client helpers.

import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export interface AthenaTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AthenaPersona {
  name: string;
  version: string;
  tagline: string;
  description: string;
}

export interface AthenaToolCall {
  name: string;
  input: Record<string, unknown>;
  outputPreview: string;
  isError: boolean;
  durationMs: number;
  round: number;
}

export interface AthenaChatReply {
  message: string;
  refused: boolean;
  usage: {
    inputTokens: number;
    outputTokens: number;
  };
  /** MP252 — Empty array when Athena didn't need tools. */
  toolCalls?: AthenaToolCall[];
  toolTruncated?: boolean;
}

export async function fetchAthenaPersona(idToken: string): Promise<AthenaPersona> {
  const res = await fetch(`${API_URL}/v1/copilot/persona`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  const body = await jsonOrThrow(res);
  return body.persona;
}

export async function askAthenaChat(
  idToken: string,
  orgId: string,
  messages: AthenaTurn[],
  // MP279 — Optional client-owned session id so the server can look
  // up sudo-mode elevation for this chat. Omitting it means the chat
  // runs unelevated (destructive tools blocked).
  sessionId?: string,
): Promise<AthenaChatReply> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/copilot/chat`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(sessionId ? { messages, sessionId } : { messages }),
    },
  );
  return jsonOrThrow(res);
}

// -------------------------------------------------------------------
// MP279 — Session elevation ("sudo mode")
// -------------------------------------------------------------------

export interface CopilotElevationStatus {
  elevated: boolean;
  expiresAt?: number;
  remainingSeconds?: number;
}

export async function getCopilotElevation(
  idToken: string,
  orgId: string,
  sessionId: string,
): Promise<CopilotElevationStatus> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/copilot/elevation?sessionId=${encodeURIComponent(sessionId)}`,
    { headers: { authorization: `Bearer ${idToken}` } },
  );
  return jsonOrThrow(res);
}

export async function grantCopilotElevation(
  idToken: string,
  orgId: string,
  sessionId: string,
): Promise<CopilotElevationStatus> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/copilot/elevation`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    },
  );
  return jsonOrThrow(res);
}

export async function revokeCopilotElevation(
  idToken: string,
  orgId: string,
  sessionId: string,
): Promise<CopilotElevationStatus> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/copilot/elevation`,
    {
      method: 'DELETE',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    },
  );
  return jsonOrThrow(res);
}

// -------------------------------------------------------------------
// MP283 — Streaming chat (SSE)
// -------------------------------------------------------------------

/** One live event from the streaming chat endpoint. Shape mirrors the
 *  server-side SSE payloads in routes/copilot.ts. */
export type AthenaStreamEvent =
  | {
      type: 'tool_call';
      name: string;
      input: Record<string, unknown>;
      round: number;
    }
  | {
      type: 'tool_result';
      name: string;
      isError: boolean;
      durationMs: number;
      outputPreview: string;
      round: number;
    }
  | { type: 'responding'; round: number }
  | {
      type: 'done';
      message: string;
      refused: boolean;
      toolCalls?: AthenaToolCall[];
      toolTruncated?: boolean;
      usage: { inputTokens: number; outputTokens: number };
    }
  | { type: 'error'; message: string };

/** Consume the SSE stream. Calls `onEvent` for every SSE frame in
 *  arrival order. Resolves when the server ends the stream; rejects on
 *  network/HTTP errors before the stream opened. A `done` or `error`
 *  event is delivered to `onEvent` too, so callers only need one
 *  callback path.
 *
 *  EventSource can't attach the Authorization header, so we use fetch
 *  + a ReadableStream reader + TextDecoder to parse the wire format
 *  ourselves. Fragmented frames across chunks are handled by keeping
 *  a rolling buffer of the tail bytes between decodes. */
export async function askAthenaChatStream(
  idToken: string,
  orgId: string,
  messages: AthenaTurn[],
  sessionId: string | undefined,
  onEvent: (event: AthenaStreamEvent) => void,
  /** The page the side panel is open on, so Athena can answer about it. */
  page?: { path: string; title?: string },
): Promise<void> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/copilot/chat/stream`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ messages, ...(sessionId ? { sessionId } : {}), ...(page ? { page } : {}) }),
    },
  );
  if (!res.ok || !res.body) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // Keep pulling chunks until the server closes. Each iteration
    // appends the decoded text to `buffer`, then peels off complete
    // SSE frames ("data: <json>\n\n"). Anything past the last frame
    // stays in the buffer for the next chunk.
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        idx = buffer.indexOf('\n\n');
        // Strip the "data: " prefix. Anything else (comments,
        // event: lines) is ignored.
        const payloadLine = raw
          .split('\n')
          .find((l) => l.startsWith('data:'));
        if (!payloadLine) continue;
        const json = payloadLine.slice('data:'.length).trim();
        if (!json) continue;
        try {
          onEvent(JSON.parse(json) as AthenaStreamEvent);
        } catch {
          // Malformed frame — drop it, keep streaming. A single
          // busted event shouldn't kill the whole session.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
